// src/api/EntityManager.js
// Generic, ontology-driven CRUD for S3 (Put/Get only) + relation listing.
// Works with ontologies defined like your README/YAML.

import {
  S3Client,
  PutObjectCommand,
  GetObjectCommand,
  HeadObjectCommand,
  ListObjectsV2Command,
} from "@aws-sdk/client-s3";

import { v4 as uuidv4 } from "uuid";

// ---- Config ----
const REGION = process.env.REACT_APP_AWS_REGION || "us-east-1";
const BUCKET_NAME =
  process.env.REACT_APP_AZRIUS_APP_BUCKET || process.env.REACT_APP_S3_BUCKET;

/** Build an S3 client with caller-supplied AWS credentials (Cognito Identity). */
export function getS3Client(awsCredentials) {
  if (!REGION) throw new Error("AWS region is not set");
  return new S3Client({ region: REGION, credentials: awsCredentials });
}

/** Fast existence check via HEAD */
async function s3HeadExists({ s3, bucket, key }) {
  try {
    await s3.send(new HeadObjectCommand({ Bucket: bucket, Key: key }));
    return true;
  } catch (e) {
    const code = e?.$metadata?.httpStatusCode || e?.statusCode;
    if (code === 404 || e?.name === "NotFound" || e?.Code === "NoSuchKey")
      return false;
    // Some accounts may get 403 for missing keys with restricted perms; treat 404/NoSuchKey only as "not exists"
    if (e?.name === "NoSuchKey") return false;
    throw e;
  }
}

/** Best-effort id resolver: prefers ontology id_pattern if available */
function resolveId(meta, { vars, fallbackPrefix = "entity" }) {
  try {
    if (meta?.idPattern) {
      return resolveTemplate(meta.idPattern, { vars });
    }
  } catch {
    // ignore and fall through to fallback
  }
  if (typeof crypto !== "undefined" && crypto?.randomUUID) {
    return `${fallbackPrefix}:${crypto.randomUUID()}`;
  }
  return `${fallbackPrefix}:${Math.random().toString(36).slice(2, 10)}`;
}

/** Stream/Body → string (browser & node friendly) */
export async function bodyToString(body) {
  if (typeof body === "string") return body;
  if (body instanceof Blob) return await body.text();
  try {
    return await new Response(body).text();
  } catch {
    return await new Promise((resolve, reject) => {
      const chunks = [];
      body.on?.("data", (c) => chunks.push(Buffer.from(c)));
      body.on?.("end", () => resolve(Buffer.concat(chunks).toString("utf-8")));
      body.on?.("error", reject);
    });
  }
}

/** Try to parse YAML if a string was passed. Accepts a JS object too. */
function normalizeOntology(ontologyLike) {
  if (typeof ontologyLike === "string") {
    const jsyaml =
      (typeof window !== "undefined" && window.jsyaml) ||
      (typeof require !== "undefined" &&
        (() => {
          try {
            return require("js-yaml");
          } catch {
            return null;
          }
        })());
    if (!jsyaml) {
      throw new Error(
        "YAML string provided but js-yaml is not available. Pass a parsed object or add js-yaml.",
      );
    }
    return jsyaml.load(ontologyLike);
  }
  return ontologyLike || {};
}

function compileOntologyMeta(ont) {
  const name = ont?.name || "Entity";
  const idPattern = ont?.id_pattern || null;

  const keyTemplate = ont?.storage?.s3?.key_template || null;
  if (!keyTemplate) {
    throw new Error(
      `[EntityManager] Missing storage.s3.key_template in ontology for "${name}".`,
    );
  }

  const properties = ont?.properties || {};
  const idPatternRegex = properties?.id?.pattern
    ? new RegExp(properties.id.pattern)
    : null;

  // NEW: compile id_pattern → { regex, tokens }
  const idExtractor = idPattern ? compileIdPattern(idPattern) : null;

  const tokenRegex = /\{([^}]+)\}/g;
  const placeholders = [];
  let m;
  while ((m = tokenRegex.exec(keyTemplate))) placeholders.push(m[1]);

  const relations = ont?.relations || {};

  return {
    name,
    keyTemplate,
    placeholders,
    idPattern, // original id_pattern
    idPatternRegex, // optional schema regex
    idCaptureRegex: idExtractor?.regex || null, // NEW
    idTokens: idExtractor?.tokens || [], // NEW
    required: Array.isArray(ont?.required) ? ont.required.slice() : [],
    properties,
    relations,
    raw: ont,
  };
}

function validateEntity(meta, entity) {
  const missing = meta.required.filter((k) => entity[k] === undefined);
  if (missing.length) {
    throw new Error(
      `[${meta.name}] Missing required fields: ${missing.join(", ")}`,
    );
  }
  if (
    meta.idPatternRegex &&
    entity.id &&
    !meta.idPatternRegex.test(entity.id)
  ) {
    throw new Error(
      `[${meta.name}] id does not match pattern ${meta.properties.id.pattern}: ${entity.id}`,
    );
  }
}

/** Resolve {tokens} inside a template using provided vars + fallbacks from entity. */
function resolveTemplate(template, { entity, vars = {} }) {
  const vmap = new Map();
  Object.keys(vars || {}).forEach((k) => {
    vmap.set(k, vars[k]);
    vmap.set(k.toLowerCase(), vars[k]);
  });
  if (entity) {
    for (const k of ["cognitoId", "ownerId", "s3Prefix", "projectId"]) {
      if (entity[k] !== undefined) {
        vmap.set(k, entity[k]);
        vmap.set(k.toLowerCase(), entity[k]);
      }
    }
  }
  if (!vmap.has("COGNITO_ID") && entity?.cognitoId) {
    vmap.set("COGNITO_ID", entity.cognitoId);
  }
  if (!vmap.has("COGNITO_ID") && entity?.ownerId?.startsWith("user:")) {
    vmap.set("COGNITO_ID", entity.ownerId.replace(/^user:/, ""));
  }
  return template.replace(/\{([^}]+)\}/g, (_, token) => {
    if (vmap.has(token)) return String(vmap.get(token));
    if (vmap.has(token.toLowerCase()))
      return String(vmap.get(token.toLowerCase()));
    throw new Error(
      `Missing template variable "${token}" for template "${template}".`,
    );
  });
}

/** Resolve {tokens} for entity key (using ontology key_template + id_pattern). */
function resolveKey(meta, { entity, vars = {} }) {
  // Drop blank values so they don't clobber parsed tokens
  const merged = sanitizeVars(vars); // already defined above

  // Which tokens do we actually need for this key_template?
  const need = new Set(meta.placeholders || []);

  // Pick an ID to parse using ontology's id_pattern
  const idToParse = entity?.id ?? merged.ID ?? merged.id;
  if (idToParse && meta.idCaptureRegex) {
    const m = meta.idCaptureRegex.exec(String(idToParse));
    if (m) {
      // Fill tokens in the order declared by id_pattern (e.g., PROJECT_UUID, ROOM_ID)
      meta.idTokens.forEach((tok, i) => {
        if (!need.has(tok)) return;
        const current = merged[tok];
        const looksLikeWholeId =
          typeof current === "string" && current.startsWith("room:");
        // Only set if missing/blank OR caller mistakenly passed the whole id
        if (current == null || current === "" || looksLikeWholeId) {
          merged[tok] = m[i + 1];
        }
      });
    }
  }

  // Best-effort for COGNITO_ID if key_template needs it and it's not supplied
  if (need.has("COGNITO_ID") && !merged.COGNITO_ID) {
    if (entity?.cognitoId) merged.COGNITO_ID = entity.cognitoId;
    else if (entity?.ownerId?.startsWith("user:")) {
      merged.COGNITO_ID = entity.ownerId.replace(/^user:/, "");
    }
  }

  // Final replacement (will throw if any required token is still missing)
  return resolveTemplate(meta.keyTemplate, { entity, vars: merged });
}

/** Merge shallow patch into entity. */
function shallowMerge(a, b) {
  return Object.assign({}, a || {}, b || {});
}

/** Stamp timestamps & bump version (best-effort optimistic version). */
function stamp(entity, { created = false } = {}) {
  const now = new Date().toISOString();
  const base = { ...entity, updatedAt: now };
  if (created && !base.createdAt) base.createdAt = now;
  const nextVersion = (typeof base.version === "number" ? base.version : 0) + 1;
  return { ...base, version: nextVersion };
}

/** Locate a relation config by name & type. */
function findRelation(meta, relationName, type) {
  const arr = meta.relations?.[relationName];
  if (!Array.isArray(arr)) return null;
  return arr.find((r) => (type ? r?.type === type : true)) || null;
}

/** List immediate child "folders" under a prefix (using Delimiter="/"). */
async function listFolders({ s3, bucket, prefix }) {
  const out = [];
  let ContinuationToken;

  do {
    const resp = await s3.send(
      new ListObjectsV2Command({
        Bucket: bucket,
        Prefix: prefix,
        Delimiter: "/",
        ContinuationToken,
        MaxKeys: 1000,
      }),
    );
    (resp.CommonPrefixes || []).forEach((cp) => {
      if (cp?.Prefix) out.push(cp.Prefix);
    });
    ContinuationToken = resp.IsTruncated
      ? resp.NextContinuationToken
      : undefined;
  } while (ContinuationToken);

  return out;
}

function escapeRegExp(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function tokensFromId(meta, idStr) {
  if (!idStr || !meta?.idCaptureRegex) return {};
  const m = meta.idCaptureRegex.exec(String(idStr));
  if (!m) return {};
  const out = {};
  meta.idTokens.forEach((tok, i) => {
    out[tok] = m[i + 1];
  });
  return out;
}

/** Build a capture regex and ordered token list from id_pattern (e.g. "room:{PROJECT_UUID}:{ROOM_ID}") */
function compileIdPattern(idPattern) {
  if (!idPattern) return null;
  const tokens = [];
  const parts = [];
  let last = 0;
  const re = /\{([A-Z0-9_]+)\}/g;
  let m;
  while ((m = re.exec(idPattern))) {
    const literal = idPattern.slice(last, m.index);
    if (literal) parts.push(escapeRegExp(literal));
    tokens.push(m[1]);
    parts.push("(.+?)"); // non-greedy: capture up to next literal
    last = m.index + m[0].length;
  }
  const tail = idPattern.slice(last);
  if (tail) parts.push(escapeRegExp(tail));
  return { regex: new RegExp("^" + parts.join("") + "$"), tokens };
}

/** Drop blank overrides so they don't clobber good derived values */
function sanitizeVars(vars = {}) {
  const out = {};
  for (const [k, v] of Object.entries(vars)) {
    if (v === undefined || v === null) continue;
    if (typeof v === "string" && v.trim() === "") continue;
    out[k] = v;
  }
  return out;
}

/**
 * Factory: build an Entity Manager for a given ontology.
 *
 * Adds list({ relation, type, vars, awsCreds }) to enumerate related entities
 * based on ontology relations[*].s3.folder_template.
 */
export function createEntityManager(ontologyLike, cfg = {}) {
  const ont = normalizeOntology(ontologyLike);
  const meta = compileOntologyMeta(ont);

  const bucketDefault = cfg.bucketName || BUCKET_NAME;
  const region = cfg.region || REGION;
  if (!bucketDefault) throw new Error("S3 bucket is not configured.");

  async function ensure({ vars, awsCreds, init }) {
    const Bucket = bucketDefault;
    const Key = resolveKey(meta, { vars });

    // Try to load
    const existing = await get({ vars, awsCreds });
    if (existing) {
      return { created: false, bucket: Bucket, key: Key, entity: existing };
    }

    // Compose prototype (defaults ← init(object|fn))
    const now = new Date().toISOString();
    let initProto = {};
    if (init) {
      initProto =
        (typeof init === "function" ? init({ meta, vars }) : init) || {};
    }

    const proto = {
      id: "0",
      type: meta.name || "Entity",
      status: "active",
      cognitoId: vars?.COGNITO_ID || vars?.userId || null,
      sessionId: String(vars?.SESSION_ID ?? vars?.sessionId ?? 0),
      messages: [],
      message_count: 0,
      scope: initProto.scope ?? vars?.SCOPE ?? "user",
      created_at: now,
      updated_at: now,
      ...initProto,
    };

    const stamped = await create({ entity: proto, vars, aws: awsCreds });

    // Single concise log on creation
    console.info(`[${meta.name}.ensure] created`, {
      key: Key,
      id: stamped?.id,
    });

    return { created: true, bucket: Bucket, key: Key, entity: stamped };
  }

  async function get({ vars, awsCreds }) {
    // console.log("[EntityMgr][get] ", { vars, awsCreds });
    const s3 = getS3Client(awsCreds);
    const Key = resolveKey(meta, { vars });

    // console.log("[EntityMgr][Key] ", { Key });
    try {
      const obj = await s3.send(
        new GetObjectCommand({
          Bucket: bucketDefault,
          Key,
          ResponseCacheControl: "no-cache",
        }),
      );
      const text = await bodyToString(obj.Body);

      // console.log("[EntityMgr][text]", text);

      return JSON.parse(text);
    } catch (err) {
      const code = err?.$metadata?.httpStatusCode;
      const name = String(err?.name || "");
      if (code === 404 || name.includes("NoSuchKey")) return null;
      console.error(`[${meta.name}.get] error`, {
        key: Key,
        code,
        name: err?.name,
        message: err?.message,
      });
      throw err;
    }
  }

  /** Use ontology id_pattern to pull tokens (e.g., PROJECT_UUID, ROOM_ID) out of entity.id */
  function deriveVarsFromEntity(meta, entity = {}) {
    const out = {};
    const need = new Set(meta.placeholders || []);

    if (entity.id && meta.idCaptureRegex) {
      const match = meta.idCaptureRegex.exec(String(entity.id));
      if (match) {
        meta.idTokens.forEach((tok, i) => {
          // only keep tokens that are actually needed by key_template
          if (need.has(tok)) out[tok] = match[i + 1];
        });
      }
    }

    // Fallbacks if not present in id (keeps your existing behavior)
    if (need.has("PROJECT_UUID") && !out.PROJECT_UUID) {
      // keep *exact* format you store (e.g., "project_..." or "project:...")
      if (entity.projectId) out.PROJECT_UUID = String(entity.projectId);
    }
    if (need.has("ROOM_ID") && !out.ROOM_ID) {
      out.ROOM_ID = entity.roomId || entity.kind;
    }
    if (need.has("ASSET_ID") && entity.assetId) out.ASSET_ID = entity.assetId;
    if (need.has("SESSION_ID") && entity.sessionId != null)
      out.SESSION_ID = entity.sessionId;

    return out;
  }

  async function put({ entity, vars = {}, aws }) {
    validateEntity(meta, entity);

    // Derive from entity.id using ontology's id_pattern
    const derived = deriveVarsFromEntity(meta, entity);

    // Non-blank overrides from caller
    const overrides = sanitizeVars(vars);

    // Ensure COGNITO_ID (template often needs it)
    if (!overrides.COGNITO_ID && aws?.identityId) {
      overrides.COGNITO_ID = aws.identityId;
    }

    // Merge: derived first, explicit overrides win when non-blank
    const mergedVars = { ...derived, ...overrides };

    const Key = resolveTemplate(meta.keyTemplate, { entity, vars: mergedVars });
    const Body = JSON.stringify(entity, null, 2);

    console.log("[EntityMgr][put]", { Key, derived, overrides });

    const s3 = getS3Client(aws);
    await s3.send(
      new PutObjectCommand({
        Bucket: bucketDefault,
        Key,
        Body,
        ContentType: "application/json",
      }),
    );
    return { Key };
  }

  async function create({ entity, vars, aws }) {
    const stamped = stamp(entity, { created: true });
    await put({ entity: stamped, vars, aws });
    return stamped;
  }

  // Helper: ensure an id exists based on ontology metadata
  function ensureEntityId({ meta, vars = {}, entity = {} }) {
    if (entity.id) return { id: entity.id, vars };

    const tpl = meta?.id_pattern || meta?.properties?.id?.pattern;

    // Pattern with tokens, e.g. "project:{UUID}"
    if (typeof tpl === "string" && tpl.includes("{")) {
      const outVars = { ...vars };
      const id = tpl.replace(/\{([A-Z0-9_]+)\}/g, (_, token) => {
        // Try supplied vars first
        if (outVars[token]) return String(outVars[token]);

        // Common token fallbacks
        if (token === "UUID" || token === "PROJECT_UUID") {
          const gen = uuidv4();
          outVars[token] = gen;
          // cross-populate common variants
          if (!outVars.projectUuid) outVars.projectUuid = gen;
          if (!outVars.UUID) outVars.UUID = gen;
          if (!outVars.PROJECT_UUID) outVars.PROJECT_UUID = gen;
          return gen;
        }
        if (token === "COGNITO_ID" && (outVars.COGNITO_ID || outVars.userId)) {
          const cid = outVars.COGNITO_ID || outVars.userId;
          outVars.COGNITO_ID = cid;
          return cid;
        }

        // Unknown token -> empty
        return "";
      });

      return { id, vars: outVars };
    }

    // Regex-like prefix, e.g. "^project:"
    if (typeof tpl === "string") {
      const prefix = tpl.replace(/[\^\$]/g, ""); // strip anchors
      const needsSep = !(
        prefix.endsWith(":") ||
        prefix.endsWith("_") ||
        prefix.endsWith("-")
      );
      const id = `${prefix}${needsSep ? ":" : ""}${uuidv4()}`;
      return { id, vars };
    }

    // Fallback
    return { id: `entity:${uuidv4()}`, vars };
  }

  async function update({ vars, patch, aws }) {
    const safeVars = { ...(vars || {}) };

    const current = (await get({ vars: safeVars, awsCreds: aws })) || null;
    const base = current || {};
    const merged = shallowMerge(base, patch);

    // Assign id on create (and prevent id changes on update)
    if (!base.id && !merged.id) {
      const { id, vars: updatedVars } = ensureEntityId({
        meta,
        vars: safeVars,
        entity: merged,
      });
      merged.id = id;
      Object.assign(safeVars, updatedVars);
    } else if (base.id) {
      merged.id = base.id;
    }

    const stamped = stamp(merged, { created: !base?.createdAt });

    console.log("✏️ Save state: ", stamped);

    await put({ entity: stamped, vars: safeVars, aws });
    return stamped;
  }

  async function softDelete({ vars, aws }) {
    const current = (await get({ vars, aws })) || {};
    const stamped = stamp({ ...current, status: "deleted" });
    await put({ entity: stamped, vars, aws });
    return stamped;
  }

  function keyFor({ entity, vars }) {
    return resolveKey(meta, { entity, vars });
  }

  async function list({ relation, type, vars, awsCreds, entity }) {
    if (!relation) {
      throw new Error(`[${meta.name}] list(): "relation" is required`);
    }

    const relCfg = findRelation(meta, relation, type);
    if (!relCfg) {
      throw new Error(
        `[${meta.name}] list(): relation "${relation}"${
          type ? ` with type "${type}"` : ""
        } not found in ontology.`,
      );
    }

    const folderTemplate = relCfg?.s3?.folder_template;
    if (!folderTemplate) {
      throw new Error(
        `[${meta.name}] list(): missing s3.folder_template for relation "${relation}"`,
      );
    }

    const bucket = relCfg?.s3?.bucket || bucketDefault;
    if (!bucket) {
      throw new Error(`[${meta.name}] list(): S3 bucket not configured`);
    }

    const prefix = resolveTemplate(folderTemplate, { entity, vars });
    const s3 = getS3Client(awsCreds);

    const prefixes = await listFolders({ s3, bucket, prefix });
    const items = prefixes.map((fullPrefix) => {
      const tail = fullPrefix.slice(prefix.length); // "<id>/"
      const id = tail.replace(/\/$/, "");
      return { id, s3_prefix: fullPrefix };
    });

    // Single concise debug line
    console.debug(`[${meta.name}.list]`, {
      relation,
      type,
      bucket,
      prefix,
      count: items.length,
    });

    return items;
  }

  return {
    meta,
    keyFor,
    get,
    ensure,
    create,
    update,
    put,
    softDelete,
    list,
    // Expose region/bucket for diagnostics
    config: { bucket: bucketDefault, region },
  };
}

/* =======================================================================
   USAGE EXAMPLE — listing projects owned by a user
   -----------------------------------------------------------------------
   const mgr = createEntityManager(userOntology, { bucketName: "azrius-data" });
   const projects = await mgr.list({
     relation: "owns",
     type: "Project",
     vars: { COGNITO_ID: identityId },  // used in folder_template
     awsCreds,                          // Cognito creds
   });
   // -> [{ id: "276bd02d-aa49-4ae2-954a-7382607752f9", s3_prefix: "geovision/.../project/<id>/" }, ...]
   ======================================================================= */

export function resolveVarsFromContext(meta, ctx = {}) {
  // If caller provides a full id like "room:{PROJECT_UUID}:{ROOM_ID}",
  // extract tokens directly from it using the ontology's id_pattern.
  const parsedFromId = ctx.id ? tokensFromId(meta, ctx.id) : {};

  // Treat "" as missing so it won't clobber parsed tokens.
  const val = (x) => (x === "" || x == null ? undefined : x);

  // Explicit ctx values win; otherwise fall back to tokens parsed from id.
  const map = {
    COGNITO_ID: val(ctx.userId),
    PROJECT_UUID: val(ctx.projectUuid) ?? val(parsedFromId.PROJECT_UUID),
    ROOM_ID: val(ctx.roomId) ?? val(parsedFromId.ROOM_ID),
    ASSET_ID: val(ctx.assetId),
    SESSION_ID: val(ctx.sessionId),
    s3Prefix: val(ctx.s3Prefix),
  };

  const vars = {};
  for (const token of meta.placeholders || []) {
    const v = map[token];
    if (v != null) vars[token] = v;
  }
  return vars;
}
